/**
 * SignupRequestView — Prospect-Facing Scheduling UI
 *
 * Displays available time slots grouped by day. Prospect selects a slot,
 * fills in name/email, and books a call. On success, shows confirmation
 * with "Add to Google Calendar" link.
 *
 * API Endpoints:
 * - GET  /api/v1/scheduling/slots?market={slug}  — Available slots
 * - POST /api/v1/scheduling/book?market={slug}    — Book a slot
 *
 * @story SCRUM-1466 — 0.1.1.8-03: SignupRequestView Frontend
 */

import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3030';

// ─── Helpers ──────────────────────────────────────────────────────

const visitorTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

function groupSlotsByDay(slots) {
  const groups = {};
  for (const slot of slots) {
    const date = new Date(slot.starts_at);
    const key = date.toLocaleDateString('en-US', {
      timeZone: visitorTimezone,
      weekday: 'long',
      month: 'short',
      day: 'numeric',
    });
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(slot);
  }
  return groups;
}

function formatSlotTime(isoString) {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: visitorTimezone,
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(new Date(isoString));
}

function formatSlotDisplay(isoString) {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: visitorTimezone,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(new Date(isoString));
}

// ─── Component ────────────────────────────────────────────────────

export default function SignupRequestView({ marketSlug, intentCode = undefined, utm = null, heading = null, body = null, refId = null }) {
  const [status, setStatus] = useState('loading');
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [bookingResult, setBookingResult] = useState(null);

  useEffect(() => {
    if (!refId) { return; }
    let cancelled = false;
    fetch(`${API_BASE_URL}/api/v1/marketing/contact-prefill?ref=${encodeURIComponent(refId)}`)
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (cancelled || !data) { return; }
        if (data.firstName) { setFirstName(data.firstName); }
        if (data.email) { setEmail(data.email); }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [refId]);

  const fetchSlots = useCallback(async () => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/v1/scheduling/slots?market=${encodeURIComponent(marketSlug)}`
      );
      const data = await res.json();

      if (!data.slots || data.slots.length === 0) {
        setSlots([]);
        setStatus('no_slots');
        return;
      }

      setSlots(data.slots);
      setStatus('selecting');
    } catch {
      setSlots([]);
      setStatus('no_slots');
    }
  }, [marketSlug]);

  useEffect(() => {
    fetchSlots();
  }, [fetchSlots]);

  const selectSlot = (slot) => {
    setSelectedSlot(slot);
    setStatus('booking_form');
  };

  const handleBook = async () => {
    if (!firstName.trim() || !email.trim()) {
      return;
    }

    setStatus('submitting');

    try {
      const res = await fetch(
        `${API_BASE_URL}/api/v1/scheduling/book?market=${encodeURIComponent(marketSlug)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            slot_id: selectedSlot.slot_id,
            intent_code: intentCode || undefined,
            email: email.trim(),
            first_name: firstName.trim(),
            utm: utm || undefined,
          }),
        }
      );

      if (res.status === 409) {
        setStatus('slot_taken');
        await fetchSlots();
        setTimeout(() => {
          setSelectedSlot(null);
          setStatus('selecting');
        }, 3000);
        return;
      }

      if (!res.ok) {
        setStatus('booking_form');
        return;
      }

      const data = await res.json();
      setBookingResult(data);
      setStatus('success');
    } catch {
      setStatus('booking_form');
    }
  };

  const slotsByDay = groupSlotsByDay(slots);

  // ─── Loading ──────────────────────────────────────────────────

  if (status === 'loading') {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto mb-4" />
        <p className="text-gray-500 text-sm">Loading available times...</p>
      </div>
    );
  }

  // ─── No Slots / SCHEDULING_NOT_CONFIGURED ─────────────────────

  if (status === 'no_slots') {
    return (
      <div className="max-w-md mx-auto px-4 py-12 text-center">
        <p className="text-gray-600 mb-4">
          No times available right now — leave your email and we&apos;ll reach out.
        </p>
        <input
          type="email"
          placeholder="your@email.com"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm
                     focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          aria-label="Email for notification"
        />
        <p className="text-xs text-gray-400 mt-2">We&apos;ll let you know when times open up.</p>
      </div>
    );
  }

  // ─── Success ──────────────────────────────────────────────────

  if (status === 'success' && bookingResult) {
    return (
      <div className="max-w-md mx-auto px-4 py-12 text-center">
        <div className="text-emerald-500 text-5xl mb-4" aria-hidden="true">&#10003;</div>
        <h2 className="text-xl font-semibold mb-2">{bookingResult.confirmation_heading || "You're booked!"}</h2>
        {bookingResult.confirmation_body && (
          <p className="text-gray-600 mb-2">{bookingResult.confirmation_body}</p>
        )}
        <p className="text-gray-600 mb-6">{bookingResult.display}</p>
        {bookingResult.add_to_calendar_url && (
          <a
            href={bookingResult.add_to_calendar_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600
                       text-white rounded-lg hover:bg-emerald-700 transition-colors
                       font-medium text-sm"
          >
            Add to Google Calendar
          </a>
        )}
      </div>
    );
  }

  // ─── Slot Grid + Booking Form ─────────────────────────────────

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      {/* SLOT_TAKEN banner */}
      {status === 'slot_taken' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
          <p className="text-amber-800 text-sm font-medium">
            That time was just taken — pick another slot below.
          </p>
        </div>
      )}

      {/* Booking form (when a slot is selected) */}
      {(status === 'booking_form' || status === 'submitting') && selectedSlot && (
        <div className="mb-8 p-5 bg-white border border-gray-200 rounded-xl shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <p className="font-medium text-gray-900">{formatSlotDisplay(selectedSlot.starts_at)}</p>
            <button
              type="button"
              onClick={() => { setSelectedSlot(null); setStatus('selecting'); }}
              className="text-sm text-emerald-600 hover:text-emerald-700"
            >
              &larr; Change
            </button>
          </div>
          <div className="space-y-3">
            <input
              type="text"
              placeholder="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm
                         focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              aria-label="First name"
              required
            />
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm
                         focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              aria-label="Email"
              required
            />
            <button
              type="button"
              onClick={handleBook}
              disabled={status === 'submitting' || !firstName.trim() || !email.trim()}
              className="w-full sm:w-auto px-6 py-2 bg-emerald-600 text-white rounded-lg
                         font-medium text-sm hover:bg-emerald-700 transition-colors
                         disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {status === 'submitting' ? 'Scheduling...' : 'Schedule Meeting'}
            </button>
          </div>
        </div>
      )}

      {/* Slot grid (always visible when selecting or slot_taken) */}
      {(status === 'selecting' || status === 'slot_taken') && (
        <>
          {heading && (
            <div className="text-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">{heading}</h2>
              {body && <p className="text-gray-600 mt-2">{body}</p>}
            </div>
          )}
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Pick a time</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 overflow-y-auto max-h-[32rem]">
            {Object.entries(slotsByDay).map(([day, daySlots]) => (
              <div key={day}>
                <p className="text-sm font-semibold text-gray-500 mb-2">{day}</p>
                <div className="flex flex-col gap-2">
                  {daySlots.map((slot) => (
                    <button
                      key={slot.slot_id}
                      type="button"
                      onClick={() => selectSlot(slot)}
                      className="w-full text-left px-4 py-2 rounded-lg border border-gray-200
                                 hover:border-emerald-500 hover:bg-emerald-50 transition-colors
                                 text-sm text-gray-700"
                    >
                      {formatSlotTime(slot.starts_at)}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

SignupRequestView.propTypes = {
  marketSlug: PropTypes.string.isRequired,
  intentCode: PropTypes.string,
  utm: PropTypes.shape({
    source: PropTypes.string,
    medium: PropTypes.string,
    campaign: PropTypes.string,
    content: PropTypes.string,
  }),
  heading: PropTypes.string,
  body: PropTypes.string,
  refId: PropTypes.string,
};

